import React from 'react';
import { FieldSelector } from 'src/components/Flowsteps/base/FilterManager';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { observer } from 'mobx-react';
import { createStyles } from '@v2/components/controls/FilterManager/ConditionEditor/ConditionEditor.styles';
import { ActionButton, ComboBox, useTheme } from '@fluentui/react';
import { RenderFieldEditorWithOperatorFunc, GetFieldOperatorsFunc } from '@v2/components/controls/FilterManager/types';
import { FilterCondition } from 'shared/types/flowstep';
import { runInAction } from 'mobx';
import { DataField } from 'shared/providers/types';

interface ConditionEditorProps {
  headerText: string;
  condition: FilterCondition;
  objectFields: DataField[];
  renderFieldEditor: RenderFieldEditorWithOperatorFunc;
  getPropOperators: GetFieldOperatorsFunc;
  showJoinOperator: boolean;
  onDeleteClick: () => void;
}

export const ConditionEditor: React.FC<ConditionEditorProps> = observer((props) => {
  const {
    condition,
    objectFields,
    renderFieldEditor,
    getPropOperators,
    headerText,
    showJoinOperator = true,
    onDeleteClick
  } = props;

  const theme = useTheme();
  const styles = createStyles(theme);

  const dataFieldToSelectorItem = (field: DataField, index: number) => ({
    ...field,
    id: `${index}`,
    fullDisplayName: field.label,
    text: field.label,
  });

  const fieldsToShow = objectFields.map(dataFieldToSelectorItem);
  const selectedField = fieldsToShow.find((field) => field.name === condition.fieldName);
  const operators = selectedField ? getPropOperators(selectedField.type) : [];

  const renderPropEditor = () => {
    if (selectedField && condition.operator) {
      return renderFieldEditor(
        selectedField,
        condition.operator,
        {
          value: condition.value,
          onChange: (value: any) => condition.value = value,
        }
      );
    }
  };

  const handleFieldChange = (treeItemId: number | null) => {
    runInAction(() => {
      treeItemId
        ? (condition.fieldName = fieldsToShow[treeItemId].name)
        : (condition.fieldName = '');
      condition.operator = '';
      condition.value = '';

      const newSelectedField = fieldsToShow.find((field) => field.name === condition.fieldName)
      condition.type = newSelectedField?.type || '';
    })
  }

  return (
    <div className={styles.conditionEditor}>
      {showJoinOperator && (
        <div className={styles.joinOperatorPanel}>
          <span>Join With</span>
          <ComboBox
            allowFreeform={false}
            text={condition.joinOperator}
            options={[
              { key: 'AND', text: 'AND' },
              { key: 'OR', text: 'OR' },
            ]}
            onChange={(event, option, index, value = 'AND') => (condition.joinOperator = value)}
          />
        </div>
      )}
      <div className={styles.header}>
        {headerText}
        <ActionButton className="delete-button" iconProps={{ iconName: 'RecycleBin' }} onClick={onDeleteClick}>
          Delete
        </ActionButton>
      </div>
      <div className={styles.inputContainer}>
        <FieldSelector
          fields={fieldsToShow}
          selectedField={selectedField}
          onSelectedFieldChanged={handleFieldChange}
          handlers={{}}
          onLoadFields={() => []}
        />
        <SearchableComboBox
          placeholder="Setup the Query Filters"
          selectedKey={condition?.operator}
          onChange={(event, item) => (condition.operator = item.key)}
          options={operators.map((description) => ({
            text: description.label,
            key: description.operator,
          }))}
        />
        {renderPropEditor()}
      </div>
    </div>
  );
});
